/**
 * Basic geometry classes: point, line, segment and boundingbox
 * All classes are interpreted as a collection of points (in the mathematical sense of the word). Functions such as intersects and contains are implemented accordingly.
 * Definitions of the classes can be found below
 */

var Geometry = {};

/**
 * A single vector/point
 * Immutable object
 * @param   {number}    x
 * @param   {number}    y
 */
Geometry.Coord = function(x,y) {
    this.x = x;
    this.y = y;
    this.taintCache();
};

Geometry.Coord.prototype.taintCache = function() {
    this.lengthCache = null;
    this.normCache = null;
    this._string = null;
};

Geometry.Coord.ORIGIN = new Geometry.Coord(0, 0);

Geometry.Coord.validator  = function(val) {
    return val instanceof Geometry.Coord && !isNaN(val.x) && !isNaN(val.y);
};


Geometry.Coord.fromPolar = function(length, angle) {
    return new Geometry.Coord(Math.cos(angle) * length, Math.sin(angle) * length);
};

Geometry.Coord.prototype.copy = function() {        // TODO: maybe rename this in clone
    return new Geometry.Coord(this.x, this.y);
};
Geometry.Coord.prototype.clone = Geometry.Coord.prototype.copy; // Clone functions is used by lib-core while cloning objects

Geometry.Coord.prototype.toString = function() {
    if(!this._string) this._string = '('+this.x+','+this.y+')';
    return this._string;
};

Geometry.Coord.prototype.getX = function() {
    return this.x;
};

Geometry.Coord.prototype.getY = function() {
    return this.y;
};

Geometry.Coord.prototype.eq = function(coord, epsilon) {
    if(epsilon === undefined) epsilon = 0;
    return Math.abs(this.x - coord.x) <= epsilon && Math.abs(this.y - coord.y) <= epsilon;
};

Geometry.Coord.prototype.order = function(coord) {
    if (this.x > coord.x) {
        return 1;
    } else if (this.x < coord.x) {
        return -1;
    } else {
        if (this.y > coord.y) {
            return 1;
        } else if (this.y < coord.y) {
            return -1;
        } else {
            return 0;
        }
    }
};

Geometry.Coord.prototype.length = function() {
    if (!this.lengthCache) {
        this.lengthCache = Math.sqrt(this.x*this.x+this.y*this.y);
    }
    return this.lengthCache;
};

Geometry.Coord.prototype.angle = function() {
    var result = Math.atan2(this.y, this.x);
    return result;
};

Geometry.Coord.prototype.angleDegrees = function() {
    return (this.angle() * 180 / Math.PI + 360) % 360;
};

Geometry.Coord.prototype.getBB = function() {
    return new Geometry.BoundingBox(this.x, this.y, 0, 0);
};

/**
 * This needs a robust implementation. Do not change it back to an exact comparison as this will break things in very
 * subtle ways!!!
 * @param coord
 * @returns {boolean}
 */
Geometry.Coord.prototype.parallel = function(coord) {
    return Math.abs(this.perp().norm().dot(coord.norm())) < 0.000000001;
};

/**
 * @param {Geometry.Coord} vec
 * @return {Geometry.Coord}
 */
Geometry.Coord.prototype.add = function(vec) {
    console.assert(vec instanceof Geometry.Coord);
    return new Geometry.Coord(this.x+vec.x,this.y+vec.y);
};
Geometry.Coord.prototype.translate = Geometry.Coord.prototype.add;

/**
 * @param {Geometry.Coord} vec
 * @return {Geometry.Coord}
 */
Geometry.Coord.prototype.sub = function(vec) {
    return new Geometry.Coord(this.x-vec.x,this.y-vec.y);
};

/**
 * @return {Geometry.Coord}
 */
Geometry.Coord.prototype.round = function(precision) {
    if(precision == undefined) precision = 1;
    return new Geometry.Coord(Math.round(this.x / precision) * precision, Math.round(this.y / precision) * precision);
};

Geometry.Coord.prototype.mult = function(facX,facY) {
    console.assert(facX !== undefined);
    if (facY === undefined) {
        return new Geometry.Coord(facX*this.x,facX*this.y);
    } else {
        return new Geometry.Coord(facX*this.x,facY*this.y);
    }
};

Geometry.Coord.prototype.norm = function() {
    if (!this.normCache) {
        this.normCache = this.mult(1/this.length());
    }
    return this.normCache;
};

Geometry.Coord.prototype.dot = function(vec) {
    return this.x*vec.x+this.y*vec.y;
};

Geometry.Coord.prototype.perp = function() {
    return new Geometry.Coord(-this.y,this.x);
};

Geometry.Coord.prototype.flipX = function() {
    return new Geometry.Coord(-this.x,this.y);
};

Geometry.Coord.prototype.flipY = function() {
    return new Geometry.Coord(this.x,-this.y);
};

Geometry.Coord.prototype.flip = function(obj) {
    if(obj instanceof Geometry.Line) {
        var flipline = obj;
        var distance = flipline.distanceVec(this);
        return new Geometry.Coord(this.x-2*distance.x, this.y-2*distance.y);
    } else if(obj instanceof Geometry.Coord) {
        var flippoint = obj;
        return flippoint.mult(2).sub(this);
    } else {
        console.error("unknown flip object");
    }
};

Geometry.Coord.prototype.rotate = function(angle, center) {
    if (!center) {
        var x = Math.cos(angle*Math.PI/180) * this.x - Math.sin(angle*Math.PI/180) * this.y;
        var y = Math.sin(angle*Math.PI/180) * this.x + Math.cos(angle*Math.PI/180) * this.y;

        return new Geometry.Coord(x,y);
    } else {
        return this.sub(center).rotate(angle).add(center);
    }
};

Geometry.Coord.prototype.transform = function(transform) {
    if(!transform) return this.copy();

    var facX = transform.flip ? -1 : 1;
    var facY = 1;
    var angle = transform.angle;
    var vec = transform.translation;

    return new Geometry.Coord(
        (Math.cos(angle*Math.PI/180)*(facX*this.x)-Math.sin(angle*Math.PI/180)*(facY*this.y))+vec.x,
        (Math.sin(angle*Math.PI/180)*(facX*this.x)+Math.cos(angle*Math.PI/180)*(facY*this.y))+vec.y
    );
};

Geometry.Coord.prototype.distance = function(p) {
    switch (p.constructor) {
        case Geometry.Coord:
            return p.sub(this).length();
        default:
            if(p.distance != undefined) {
                return p.distance(this);
            } else {
                throw new Error("Unknown object");
            }
    }
    return null;
};

Geometry.Coord.prototype.distanceVec = function(p) {
    switch (p.constructor) {
        case Geometry.Coord:
            return p.sub(this);
        default:
            if(p.distanceVec != undefined) {
                return p.distanceVec(this).mult(-1);
            } else {
                throw new Error("Unknown object");
            }
    }
    return null;
};

Geometry.Coord.prototype.containedByBB = function(bb) {
    return this.x >= bb.getXMin() && this.x <= bb.getXMax()
        && this.y >= bb.getYMin() && this.y <= bb.getYMax();
};

/** Only for debug */
Geometry.Coord.prototype.draw = function(svg,parent,color,width,opacity) {
    svg.rect(parent,this.x-2.5,this.y-2.5,5,5,{'stroke' : color, 'strokeWidth': width, 'opacity' : opacity});
};



/**
 * A line with infinite length
 * Mathematical definition: A line is the collection of all points at equal distance of two (imaginary) points
 * The line datamodel is defined by a direction (vector) and a point that is on the line
 * Immutable object
 * @param   {Geometry.Coord}    dir
 * @param   {Geometry.Coord}    point
 */
Geometry.Line = function(dir,point) {
    this.dir = dir;
    this.point = point;
};
Geometry.Line.HOR = new Geometry.Coord(1,0);
Geometry.Line.VER = new Geometry.Coord(0,1);

Geometry.Line.validator = function(line) {
    return Geometry.Coord.validator(line.dir)
        && Geometry.Coord.validator(line.point)
        && !line.dir.eq(Geometry.Coord.ORIGIN);
};

Geometry.Line.lineThrough = function(p1, p2) {
    return new Geometry.Line(p2.sub(p1), p1);
};

Geometry.Line.prototype.copy = function() {
    return new Geometry.Line(this.dir.copy(),this.point.copy());
};
Geometry.Line.prototype.clone = Geometry.Line.prototype.copy;

Geometry.Line.prototype.taintCache = function() {
    this._string = null;
};

Geometry.Line.prototype.toString = function() {
    if(!this._string) this._string = '{'+this.dir.toString()+','+this.point.toString()+'}';
    return this._string;
};

Geometry.Line.prototype.order = function(line) {
    if (this.dir.order(line.dir) > 0) {
        return 1;
    } else if (this.dir.order(line.dir) < 0) {
        return -1;
    } else if (this.point.order(line.point) > 0) {
        return 1;
    } else if (this.point.order(line.point) < 0) {
        return -1;
    } else {
        return 0;
    }
};

Geometry.Line.prototype.getDir = function() {
    return this.dir.norm();
};

Geometry.Line.prototype.isVert = function() {
    return this.dir.parallel(Geometry.Line.VER);
};

Geometry.Line.prototype.isHor = function() {
    return this.dir.parallel(Geometry.Line.HOR);
};

Geometry.Line.prototype.parameter = function(v) {
    return v.sub(this.point).dot(this.dir)/this.dir.dot(this.dir);
};

Geometry.Line.prototype.compareCoords = function(p1, p2) {
    return this.parameter(p1) - this.parameter(p2);
};

Geometry.Line.prototype.getPoint = function(parameter) {
    return this.dir.mult(parameter).add(this.point);
};

Geometry.Line.prototype.getBB = function() {
    if (this.dir.x == 0) {
        return new Geometry.BoundingBox(this.point.x,Number.NEGATIVE_INFINITY,0,Number.POSITIVE_INFINITY);
    } else if (this.dir.y == 0) {
        return new Geometry.BoundingBox(Number.NEGATIVE_INFINITY,this.point.y,Number.POSITIVE_INFINITY,0);
    } else {
        return new Geometry.BoundingBox(Number.NEGATIVE_INFINITY,Number.NEGATIVE_INFINITY,Number.POSITIVE_INFINITY,Number.POSITIVE_INFINITY);
    }
};

/**
 * This needs a robust implementation. Do not change it back to an exact comparison as this will break things in very
 * subtle ways!!!
 * @param coord
 * @returns {boolean}
 */
Geometry.Line.prototype.parallel = function(obj) {
    switch (obj.constructor) {
        case Geometry.Line:
            return Math.abs(this.dir.perp().norm().dot(obj.dir.norm())) < 0.0000000001;
        case Geometry.Segment:
            return this.parallel(obj.line());
        default:
            throw new Error("Unkown object");
    }
};

Geometry.Line.prototype.translate = function(vec) {
    return new Geometry.Line(this.dir.copy(),this.point.add(vec));
};

Geometry.Line.prototype.intersects = function(obj) {
    switch(obj.constructor) {
        case Geometry.Coord:
            return this.contains(obj);
        case Geometry.Line:
            return !this.parallel(obj) || this.contains(obj.point);
        case Geometry.BoundingBox:
            return this.intersectsBB(obj);
        default:
            if(obj.intersects != undefined) {
                return obj.intersects(this);
            } else {
                throw new Error("Unknown object");
            }
    }
};

Geometry.Line.prototype.intersectsBB = function(bb) {
    var diagonals = bb.getDiagonals();
    return this.intersects(diagonals[0]) || this.intersects(diagonals[1]);
};

Geometry.Line.prototype.intersection = function(obj) {
    switch (obj.constructor) {
        case Geometry.Line:
            if(this.parallel(obj)) {
                if(this.contains(obj.point))
                    return obj.point;
                else
                    return null;
            }
            var a,b;
            if(this.order(obj)<0) {
                a = this;
                b = obj;
            } else {
                a = obj;
                b = this;
            }
            var d0 = a.dir;
            var d1 = b.dir;
            var p0 = a.point;
            var p1 = b.point;
            var ti = -p0.sub(p1).dot(d1.perp())/d0.dot(d1.perp());
            console.assert(!isNaN(ti));
            return p0.add(d0.mult(ti));
        default:
            if(obj.intersection != undefined) {
                return obj.intersection(this);
            } else {
                throw new Error("Unknown object");
            }
    }
};

Geometry.Line.prototype.contains = function(obj) {
    switch(obj.constructor) {
        case Geometry.Line:
            return this.parallel(obj) && this.contains(obj.point);
        case Geometry.Segment:
            return this.contains(obj.a) && this.contains(obj.b);
        case Geometry.Coord:
            return Geometry.collinear(this.point, this.point.add(this.dir), obj);
        default:
            throw new Error("Unknown object");
    }
};

Geometry.Line.prototype.containedByBB = function(bb) {
    return false;
};

Geometry.Line.prototype.closestPointToPoint = function(point) {
    var line = new Geometry.Line(this.dir.perp(),point);
    return this.intersection(line);
};

Geometry.Line.prototype.distance = function(object) {
    return this.distanceVec(object).length();
};

Geometry.Line.prototype.distanceVec = function(object) {
    var self = this;
    var closest;
    switch (object.constructor) {
        case Geometry.Coord:
            closest = self.closestPointToPoint(object);
            return object.sub(closest);
        case Geometry.Line:
            if (!self.parallel(object)) {
                return new Geometry.Coord(0,0);
            } else {
                closest = self.closestPointToPoint(object.point);
                return object.point.sub(closest);
            }
            break;
        default:
            if(object.distanceVec != undefined) {
                return object.distanceVec(this).mult(-1);
            } else {
                throw new Error("Unknown object");
            }
    }
};

/** Only for debug */
Geometry.Line.prototype.draw = function(svg,parent,color,width,opacity) {
    var line = new Geometry.Line(this.dir.norm(), this.point); // normalized dir
    var start = line.getPoint(-1000000);
    var   end = line.getPoint(1000000);
    svg.line(parent,start.x,start.y,end.x,end.y,{'stroke' : color, 'strokeWidth': width, 'opacity' : opacity});
};


/**
 * A ray is a infinite section of a line defined by one enpoint
 * Due to the limited use of this class, not all functions are implemented
 * Immutable object
 * @param   {Geometry.Coord}    dir
 * @param   {Geometry.Coord}    point
 */
Geometry.Ray = function(dir,point) {
    this.dir = dir;
    this.point = point;
};

Geometry.Ray.prototype.taintCache = function() {
    this._raySegment = null;
    this._line = null;
};

Geometry.Ray.prototype.copy = function() {
    return new Geometry.Ray(this.dir.copy(),this.point.copy());
};
Geometry.Ray.prototype.clone = Geometry.Ray.prototype.copy;


Geometry.Ray.prototype.line = function(){
    if(!this._line) this._line = new Geometry.Line(this.dir, this.point);
    return this._line;
};

Geometry.Ray.prototype._getRaySegment = function(){
    if(!this._raySegment)
        this._raySegment = new Geometry.Segment(this.point, this.point.add(this.dir));
    return this._raySegment;
};
Geometry.Ray.prototype.parameter = function(point) {
    return this.line().parameter(point);
};

Geometry.Ray.prototype.parameterWithinRange = function(point) {
    return this.parameter(point) >= 0;
};

Geometry.Ray.prototype.parameterWithinRangeExcludingPoint = function(point) {
    return this.parameter(point) > 0;
};

Geometry.Ray.prototype.intersects = function(obj) {
    switch(obj.constructor) {
        case Geometry.Segment:
            var s1 = Geometry.pointLineSide(this._getRaySegment().a, this._getRaySegment().b, obj.a);
            var s2 = Geometry.pointLineSide(this._getRaySegment().a, this._getRaySegment().b, obj.b);
            //for a regular intersection obj.a and obj.b must be on opposite sides of this ray (and not collinear)
            if(s1 !== s2 && s1 !== 0 && s2 !== 0) {
                var s3 = Geometry.pointLineSide(this._getRaySegment().a, this._getRaySegment().b, obj.a);
                var s4 = Geometry.pointLineSide(this._getRaySegment().a, obj.b, obj.a);
                //and b and obj.b must be on the same side of the line through a and obj.a (or a, obj.b and obj.a are collinear)
                return  s3 === s4 || s4 === 0;
            } else if(s1 === 0 || s2 === 0) {
                //obj.a or obj.b is collinear with ray
                //any of the collinear points must be on the ray
                return (s1 == 0 && this.parameterWithinRange(obj.a)) || (s2 == 0 && this.parameterWithinRange(obj.b));
            } else {//s1 == s2 && s1 != 0 && s2 != 0
                //both obj.a and obj.b at same side of ray
                return false;
            }
            break;
        default:
            if(obj.intersects != undefined) {
                return obj.intersects(this);
            } else {
                throw new Error("Unknown object");
            }
    }
};

Geometry.Ray.prototype.intersection = function (obj) {
    var intersection = this.line().intersection(obj);
    if (intersection && this.parameterWithinRange(intersection)) {
        return intersection;
    } else {
        return null;
    }
};

/**
 * Used for polygon crossing number calculation
 * http://geomalgorithms.com/a03-_inclusion.html
 * @param  {[type]} obj [description]
 * @return {[type]}     [description]
 */
Geometry.Ray.prototype.specialSegmentIntersects = function(segment) {
    var s1 = Geometry.pointLineSide(this._getRaySegment().a, this._getRaySegment().b, segment.a);
    var s2 = Geometry.pointLineSide(this._getRaySegment().a, this._getRaySegment().b, segment.b);
    //for a regular intersection obj.a and obj.b must be on opposite sides of this ray (and not collinear)
    if(s1 !== s2 && s1 !== 0 && s2 !== 0) {
        var s3 = Geometry.pointLineSide(this._getRaySegment().a, this._getRaySegment().b, segment.a);
        var s4 = Geometry.pointLineSide(this._getRaySegment().a, segment.b, segment.a);
        //and b and obj.b must be on the same side of the line through a and obj.a (and a, obj.a and obj.b not collinear)
        return  s3 === s4;
    } else if(s1 === 0 && s2 === 0) {
        //obj.a and obj.b are collinear with ray
        return false;
    } else if(s1 === 0) {
        //obj.a is collinear
        //obj.a must be on ray and obj.b to the right of ray
        return this.parameterWithinRangeExcludingPoint(segment.a) && s2 > 0;
    } else if(s2 === 0) {
        //obj.b is collinear
        //obj.b must be on ray and obj.a to the right of ray
        return this.parameterWithinRangeExcludingPoint(segment.b) && s1 > 0;
    } else {//s1 == s2 && s1 != 0 && s2 != 0
        //both obj.a and obj.b at same side of ray
        return false;
    }
};


/**
 * A segment or line with finite length
 * Mathematical definition: A segment is the collection of all points of a finite section of a line
 * Defined by two endpoints
 * Immutable object
 * @constructor
 * @param   {Geometry.Coord}    a
 * @param   {Geometry.Coord}    b
 */
Geometry.Segment = function(a,b) {
    this.a = a;
    this.b = b;
};

Geometry.Segment.validator = function(seg) {
    return Geometry.Coord.validator(seg.a) && Geometry.Coord.validator(seg.b);
};

Geometry.Segment.prototype.taintCache = function() {
    this._line = null;
};

Geometry.Segment.prototype.eq = function (seg) {
    return this.a.eq(seg.a) && this.b.eq(seg.b);
};

Geometry.Segment.prototype.copy = function() {
    return new Geometry.Segment(this.a.copy(),this.b.copy());
};
Geometry.Segment.prototype.clone = Geometry.Segment.prototype.copy;


Geometry.Segment.prototype.order = function(line) {
    if (this.a.order(line.a) > 0) {
        return 1;
    } else if (this.a.order(line.a) < 0) {
        return -1;
    } else if (this.b.order(line.b) > 0) {
        return 1;
    } else if (this.b.order(line.b) < 0) {
        return -1;
    } else {
        return 0;
    }
};

Geometry.Segment.prototype.getCenter = function() {
    var x = (this.a.x+this.b.x)/2;
    var y = (this.a.y+this.b.y)/2;
    return new Geometry.Coord(x, y);
};

Geometry.Segment.prototype.getBB = function () {
    var x = Math.min(this.a.x, this.b.x);
    var y = Math.min(this.a.y, this.b.y);
    var w = Math.abs(this.a.x-this.b.x);
    var h = Math.abs(this.a.y-this.b.y);
    var bb = new Geometry.BoundingBox(x, y, w, h);
    return bb;
};

Geometry.Segment.prototype.toString = function() {
    if(!this._string) this._string = 'Segment('+this.a.toString()+','+this.b.toString()+')';
    return this._string;
};

Geometry.Segment.prototype.isLengthZero = function() {
    return this.a.eq(this.b);
};

Geometry.Segment.prototype.length = function() {
    return this.a.sub(this.b).length();
};

Geometry.Segment.prototype.scale = function(facX,facY) {
    var result = new Geometry.Segment(this.a.mult(facX,facY),this.b.mult(facX,facY));
    return result;
};

Geometry.Segment.prototype.flip = function(flip){
    return this.scale(flip,1);
};

Geometry.Segment.prototype.translate = function(vec) {
    var result = new Geometry.Segment(this.a.add(vec),this.b.add(vec));
    return result;
};

Geometry.Segment.prototype.rotate = function(angle) {
    var result = new Geometry.Segment(this.a.rotate(angle),this.b.rotate(angle));
    return result;
};

Geometry.Segment.prototype.line = function(){
    if(!this._line) this._line = new Geometry.Line(this.b.sub(this.a), this.a);
    return this._line;
};

Geometry.Segment.prototype.direction = function() {
    return this.a.sub(this.b);
};

Geometry.Segment.prototype.parameter = function(point) {
    return this.line().parameter(point);
};

Geometry.Segment.prototype.parameterWithinRange = function(point) {
    var param = this.parameter(point);
    return param >= 0 && param <= 1;
};

Geometry.Segment.prototype.getPoint = function(parameter) {
    return this.b.sub(this.a).mult(parameter).add(this.a);
};

Geometry.Segment.prototype.contains = function(obj, epsilon) {
    if(epsilon == undefined) epsilon = 1e-6;
    switch (obj.constructor) {
        case Geometry.Coord:
            if (Geometry.collinear(this.a,this.b,obj)) {
                if (this.parameterWithinRange(obj)) {
                    return true;
                }
                if (this.a.eq(obj, epsilon) || this.b.eq(obj, epsilon)) {
                    return true;
                }
            }
            break;
        case Geometry.Segment:
            if(this.collinear(obj)) {
                if(this.contains(obj.a, epsilon) && this.contains(obj.b, epsilon))
                    return true;
            }
            break;
        default:
            throw new Error("unknown object");
    }

    return false;
};

Geometry.Segment.prototype.parallel = function(obj) {
    switch(obj.constructor) {
        case Geometry.Segment:
            return this.b.sub(this.a).parallel(obj.b.sub(obj.a));
        case Geometry.Line:
            return this.b.sub(this.a).parallel(obj.dir);
        default:
            throw new Error("Unknown object");
    }
};

Geometry.Segment.prototype.intersects = function(obj) {
    switch(obj.constructor) {
        case Geometry.Segment:
            var s1 = Geometry.pointLineSide(this.a, this.b, obj.a);
            var s2 = Geometry.pointLineSide(this.a, this.b, obj.b);
            //for a regular intersection obj.a and obj.b must be on opposite sides of the line through this.a and this.b (at most one of them may be collinear)
            if(s1 !== s2) {
                var s3 = Geometry.pointLineSide(obj.a, obj.b, this.a);
                var s4 = Geometry.pointLineSide(obj.a, obj.b, this.b);
                //and this.a and this.b must be on opposite sides of the line through obj.a and obj.b
                return s3 !== s4;
            } else { //s1 === s2
                if(s1 === 0) {  //special case: this.a, this.b, obj.a and obj.b are collinear
                    return this.parameterWithinRange(obj.a) || this.parameterWithinRange(obj.b);
                } else {
                    return false;
                }
            }
            break;
        case Geometry.Line:
            var a = obj.point;
            var b = obj.point.add(obj.dir);
            //for an intersection this.a and this.b must be on opposite sides of the line or on it
            var s1 = Geometry.pointLineSide(a, b, this.a);
            var s2 = Geometry.pointLineSide(a, b, this.b);
            return s1 !== s2;
        case Geometry.BoundingBox:
            return this.intersectsBB(obj);
        default:
            if(obj.intersects != undefined) {
                return obj.intersects(this);
            } else {
                throw new Error("Unknown object");
            }
    }
};

Geometry.Segment.prototype.intersection = function(obj){
    var intersection = null;
    switch(obj.constructor) {
        case Geometry.Segment:
            var line1 = this.line();
            var line2 = obj.line();
            if(!line1.parallel(line2)) {
                intersection = line1.intersection(line2);
                if(intersection && this.parameterWithinRange(intersection) && obj.parameterWithinRange(intersection))
                    return intersection;
            } else {
                if(obj.b.eq(this.a) || obj.a.eq(this.a)) return this.a.copy();
                if(obj.b.eq(this.b) || obj.a.eq(this.b)) return this.b.copy();
            }
            return null;
        case Geometry.Line:
            var line = this.line();
            intersection = obj.intersection(line);
            if(intersection && this.parameterWithinRange(intersection))
                return intersection;
            return null;
        default:
            if(obj.intersection != undefined) {
                return obj.intersection(this);
            } else {
                throw new Error("Unknown object");
            }
    }
};

Geometry.Segment.prototype.closestPointToPoint = function(point) {
    var closestPoint = this.line().closestPointToPoint(point);
    var closesParam = this.parameter(closestPoint);
    if (closesParam < 0) {
        return this.a;
    } else if (closesParam > 1) {
        return this.b;
    } else {
        return closestPoint;
    }
};

Geometry.Segment.prototype.intersections = function(obj) {
    switch(obj.constructor) {
        case Geometry.BoundingBox:
            return this.intersectionsBB(obj);
        default:
            if(obj.intersection != undefined) {
                return obj.intersections(this);
            } else {
                throw new Error("Unknown object");
            }
    }
};

/**
 * Returns intersection points.
 *
 * @param  {object} bb Bounding box
 * @return {array} Array contains intersection points.
 *
 * 1) Array contains one element  - single intersection point exists
 * 2) Array contains two element - two intersection point exist nearest (1st index) and farthest (2nd index) respectively
 * nearest is defined by tmin and farthest is defined by tmax
 *
 * In case of no intersections return empty array
 */
Geometry.Segment.prototype.intersectionsBB = function(bb) {
    console.assert(bb instanceof Geometry.BoundingBox);
    var tmin, tmax, txmin, txmax, tymin, tymax, box;
    var intersections = [];

    //create ray and check whether this intersects with the box or not
    var ray = this.line(),
        lineDir = ray.getDir(),
        lineEnd = ray.point.add(ray.dir);

    //modified bbox
    box = {
        min: bb.getMinMax(),
        max: bb.getMaxMin()
    };

    //x-dim
    txmin = (box.min.x - ray.point.x) / lineDir.x;
    txmax = (box.max.x - ray.point.x) / lineDir.x;

    if(txmin > txmax) {
        var t = txmin, txmin = txmax, txmax = t;
    }

    //y-dim
    tymin = (box.min.y - ray.point.y) / lineDir.y;
    tymax = (box.max.y - ray.point.y) / lineDir.y;

    if(tymin > tymax) {
        var t = tymin, tymin = tymax, tymax = t;
    }

    //NO INTERSECTIONS
    if(tymax < txmin || tymin > txmax) return intersections;

    //update tmin and tmax
    //tmin and tmax define nearest and farthest intersection respectively
    tmin = Math.max(txmin, tymin);
    tmax = Math.min(txmax, tymax);

    //we can extend ray-aabb intersection test to segment-aabb intersection test
    //if nearest intersection length is more than the actual ray length, segment defined by ray is not intersected
    //case: both end are outside
    //NO INTERSECTIONS
    if(tmin > this.length()) return intersections;

    var isInBox = bb.contains(ray.point);

    //case 1: ray starts inside and crosses bbox boundary
    //one intersection point exist and defined by tmax
    if(isInBox && tmax <= this.length()) {
        intersections.push(new Geometry.Coord(ray.point.x + lineDir.x * tmax, ray.point.y + lineDir.y * tmax));
    }
    //case 2: ray starts outside and crosses bbox boundary
    //either one or two intersection (nearest and farthest) points exist
    else if(!isInBox && tmin >= 0) {
        intersections.push(new Geometry.Coord(ray.point.x + lineDir.x * tmin, ray.point.y + lineDir.y * tmin));

        //if the line crosses two boundaries
        if(tmax <= this.length()) {
            intersections.push(new Geometry.Coord(ray.point.x + lineDir.x * tmax, ray.point.y + lineDir.y * tmax));
        }
    }

    //empty in case the ray is away from box (tmin < 0), NO INTERSECTIONS
    return intersections;
};

//    used for poly union
Geometry.Segment.prototype.specialIntersections = function(seg){
    console.assert(seg instanceof Geometry.Segment);
    var result = [];
    var self = this;
    var a = self.a;
    var b = self.b;
    var c = seg.a;
    var d = seg.b;

    var ab = self.line();
    var cd = seg.line();

    if (ab.parallel(cd)) {
        if (self.contains(c)) result.push(c);
        if (self.contains(d)) result.push(d);
        if (seg.contains(a)) result.push(a);
        if (seg.contains(b)) result.push(b);
        return result;
    } else {

        var inter;
        if (ab.order(cd) < 0) {
            inter = ab.intersection(cd);
        } else {
            inter = cd.intersection(ab);
        }

        if (self._inBBinter(inter) && seg._inBBinter(inter)) {
            result.push(inter);
            return result;
        } else {
            return result;
        }
    }
};

Geometry.Segment.prototype.collinear = function(seg) {
    return Geometry.collinear(this.a, this.b, seg.a)
        && Geometry.collinear(this.a, this.b, seg.b);
};

// TODO: I think overlap is enough here (intersect is not a subset of overlap)
Geometry.Segment.prototype.overlapsWithSegmentInMoreThanOnePoint = function(seg, epsilon) {
    console.assert(seg instanceof Geometry.Segment);
    if(epsilon == undefined) epsilon = 1e-6;
    if(this.collinear(seg)) {
        var pa = this.parameter(seg.a);
        var pb = this.parameter(seg.b);
        var pmin = Math.min(pa, pb);
        var pmax = Math.max(pa, pb);
        return pmin < 1-epsilon && pmax > 0+epsilon && !seg.a.eq(seg.b, epsilon) && !this.a.eq(this.b, epsilon);
    } else {
        return false;
    }
};

Geometry.Segment.prototype._inBBinter = function(point) {
    var result;
    var xmin = Math.min(this.a.x, this.b.x);
    var xmax = Math.max(this.a.x, this.b.x);
    var ymin = Math.min(this.a.y, this.b.y);
    var ymax = Math.max(this.a.y, this.b.y);

    if (ymin === ymax) {
        result =  (point.x <= xmax) && (point.x >= xmin);
    } else if (xmin === xmax) {
        result = (point.y <= ymax) && (point.y >= ymin);
    } else {
        result = (point.x <= xmax) && (point.x >= xmin) && (point.y <= ymax) && (point.y >= ymin);
    }
    return result;
};

/**
 * Returns whether a segment intersects the bbox.
 * @param  {object} bb Bounding box
 * @return {boolean}    true or false
 */
Geometry.Segment.prototype.intersectsBB = function(bb) {
    console.assert(bb instanceof Geometry.BoundingBox);
    //one of the segment end is inside bbox
    if(bb.contains(this.a) || bb.contains(this.b)) return true;
    //intersects one of the diagonals
    var diagonals = bb.getDiagonals();
    return this.intersects(diagonals[0]) || this.intersects(diagonals[1]);
};

Geometry.Segment.prototype.containedByBB = function(bb) {
    return bb.contains(this.a) && bb.contains(this.b);
};

Geometry.Segment.prototype.distance = function(object){
    var result = this.distanceVec(object).length();
    return result;
};

Geometry.Segment.prototype.distanceVec = function(object){
    var line = new Geometry.Line(this.b.sub(this.a),this.a);

    switch (object.constructor) {
        case Geometry.Coord:
            return object.sub(this.closestPointToPoint(object));
        case Geometry.Line:
            if (line.parallel(object)) {
                return this.a.distanceVec(object);
            }
            var intersection = object.intersection(line);
            var closesParam = this.parameter(intersection);

            if (closesParam < 0) {
                return this.a.distanceVec(object);
            } else if (closesParam > 1) {
                return this.b.distanceVec(object);
            } else {
                return new Geometry.Coord(0,0);
            }
            break;
        case Geometry.Segment:
            var vec = this.distanceVec(object.a);
            var v1 = this.distanceVec(object.b);
            if(v1.length() < vec.length())
                vec = v1;
            var v2 = object.distanceVec(this.a).mult(-1);
            if(v2.length() < vec.length())
                vec = v2;
            var v3 = object.distanceVec(this.b).mult(-1);
            if(v3.length() < vec.length())
                vec = v3;
            return vec;
        default:
            if(object.distanceVec != undefined) {
                return object.distanceVec(this).mult(-1);
            } else {
                throw new Error("Unknown object");
            }
    }
};

/** Only for debug */

Geometry.Segment.prototype.closeTo = function(obj, dist) {
    return this.distance(obj) < dist;
};

Geometry.Segment.prototype.draw = function(svg,parent,color,width,opacity) {
    var start = this.a;
    var   end = this.b;
    svg.line(parent,start.x,start.y,end.x,end.y,{'stroke' : color, 'strokeWidth': width, 'opacity' : opacity});
};


/**
 * A boundingbox is a filled rectangle alligned with the x-y-axis.
 * Mathematical definition: A boundingbox is the collection of all points (p) with property p.x in [bb.x, bb.x+bb.width] and p.y in [bb.y, bb.y+bb.height] (i.e. including the boundary)
 * Datamodel defined by x and y coordinates of one corner point of the rectangle and positive width and height
 * Width and height are allowed to be -infinity in the case of a boundingbox that contains no points
 * Immutable object
 * @param {[type]} x      [description]
 * @param {[type]} y      [description]
 * @param {[type]} width  [description]
 * @param {[type]} height [description]
 */
Geometry.BoundingBox = function(x,y,width,height) {
    this.x = x;
    this.y = y;
    this.width  = width;
    this.height = height;
};
Geometry.BoundingBox.emptyBoundingBox = new Geometry.BoundingBox(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);

Geometry.BoundingBox.prototype.toString = function() {
    return "BoundingBox("+this.x+","+this.y+',w='+this.width+",h="+this.height+")";
};

Geometry.BoundingBox.prototype.taintCache = function() {
    this._poly = null;
    this._minMin = null;
    this._maxMin = null;
    this._minMax = null;
    this._maxMax = null;
    this._diagonals = null;
};

Geometry.BoundingBox.fromCoordinates = function(xMin, yMin, xMax, yMax) {
    console.assert(!isNaN(xMin));
    console.assert(!isNaN(yMin));
    console.assert(!isNaN(xMax));
    console.assert(!isNaN(yMax));
    var width = xMax == Number.NEGATIVE_INFINITY ? Number.NEGATIVE_INFINITY : xMax-xMin;
    var height = yMax == Number.NEGATIVE_INFINITY ? Number.NEGATIVE_INFINITY : yMax-yMin;
    return new Geometry.BoundingBox(xMin, yMin, width, height);
};

Geometry.BoundingBox.prototype.eq = function(other) {
    return this.x == other.x && this.y == other.y && this.width == other.width && this.height == other.height;
};

Geometry.BoundingBox.prototype.getXMin = function() {
    return this.x;
};

Geometry.BoundingBox.prototype.getXMax = function() {
    if(this.width == Number.NEGATIVE_INFINITY) return Number.NEGATIVE_INFINITY;
    else if(this.width == Number.POSITIVE_INFINITY) return Number.POSITIVE_INFINITY;
    return this.x + this.width;
};

Geometry.BoundingBox.prototype.getYMin = function() {
    return this.y;
};

Geometry.BoundingBox.prototype.getYMax = function() {
    if(this.height == Number.NEGATIVE_INFINITY) return Number.NEGATIVE_INFINITY;
    else if(this.height == Number.POSITIVE_INFINITY) return Number.POSITIVE_INFINITY;
    return this.y + this.height;
};

Geometry.BoundingBox.prototype._getMinMin = function() {
    return new Geometry.Coord(this.getXMin(),this.getYMin());
};

Geometry.BoundingBox.prototype.getMinMin = function() {
    if(!this._minMin) this._minMin = this._getMinMin();
    return this._minMin;
};

Geometry.BoundingBox.prototype._getMinMax = function() {
    return new Geometry.Coord(this.getXMin(),this.getYMax());
};

Geometry.BoundingBox.prototype.getMinMax = function() {
    if(!this._minMax) this._minMax = this._getMinMax();
    return this._minMax;
};

Geometry.BoundingBox.prototype._getMaxMin = function() {
    return new Geometry.Coord(this.getXMax(),this.getYMin());
};

Geometry.BoundingBox.prototype.getMaxMin = function() {
    if(!this._maxMin) this._maxMin = this._getMaxMin();
    return this._maxMin;
};

Geometry.BoundingBox.prototype._getMaxMax = function() {
    return new Geometry.Coord(this.getXMax(),this.getYMax());
};

Geometry.BoundingBox.prototype.getMaxMax = function() {
    if(!this._maxMax) this._maxMax = this._getMaxMax();
    return this._maxMax;
};

Geometry.BoundingBox.prototype._getDiagonals = function() {
    return [new Geometry.Segment(this.getMinMin(), this.getMaxMax()),
            new Geometry.Segment(this.getMinMax(), this.getMaxMin())];
};

Geometry.BoundingBox.prototype.getDiagonals = function() {
    if(!this._diagonals) this._diagonals = this._getDiagonals();
    return this._diagonals;
};

Geometry.BoundingBox.prototype.getCenter = function() {
    return new Geometry.Coord(this.x+this.width/2,this.y+this.height/2);
};

Geometry.BoundingBox.prototype.getArea = function() {
    return this.width * this.height;
};

//WARNING: does not scale wrt origin (like other scale functions) but wrt center of boundingbox
Geometry.BoundingBox.prototype.scale = function(fac) {
    return new Geometry.BoundingBox(this.x-0.5*(fac-1)*this.width, this.y-0.5*(fac-1)*this.height, fac * this.width, fac * this.height);
};

Geometry.BoundingBox.prototype.buffer = function(a) {
    return new Geometry.BoundingBox(this.x-a, this.y-a, this.width+2*a, this.height+2*a);
};

Geometry.BoundingBox.prototype.intersection = function(bb) {
    var result = new Geometry.Polygon();
    var x1 = Math.max(this.getMinMin().x, bb.getMinMin().x);
    var y1 = Math.max(this.getMinMin().y, bb.getMinMin().y);
    var x2 = Math.min(this.getMaxMax().x, bb.getMaxMax().x);
    var y2 = Math.min(this.getMaxMax().y, bb.getMaxMax().y);
    result.points.push(new Geometry.Coord(x1,y1));
    result.points.push(new Geometry.Coord(x2,y1));
    result.points.push(new Geometry.Coord(x2,y2));
    result.points.push(new Geometry.Coord(x1,y2));
    return result;
};

Geometry.BoundingBox.prototype.flip = function(flip) {
    if(flip === 1) return this; //nothing to do
    return new Geometry.BoundingBox(-this.getXMax(), this.y, this.width, this.height);
};

Geometry.BoundingBox.prototype.translate = function(vec) {
    if(vec.x === 0 && vec.y === 0) return this; //nothing to do
    return new Geometry.BoundingBox(this.x + vec.x, this.y + vec.y, this.width, this.height);
};

Geometry.BoundingBox.prototype.rotate = function(angle, center) {
    if(angle === 0) return this; //nothing to do
    return this.getPoly().rotate(angle, center).getBB();
};

Geometry.BoundingBox.prototype.centerAround = function(center){
    return new Geometry.BoundingBox(center.x-this.width/2,center.y-this.height/2,this.width,this.height);
};

Geometry.BoundingBox.prototype.merge = function(bb) {
    if (!bb) return this;

    var minX = Math.min( this.getXMin(), bb.getXMin() );
    var minY = Math.min( this.getYMin(), bb.getYMin() );
    var maxX = Math.max( this.getXMax(), bb.getXMax() );
    var maxY = Math.max( this.getYMax(), bb.getYMax() );

    return new Geometry.BoundingBox.fromCoordinates(minX, minY, maxX, maxY);
};

Geometry.BoundingBox.prototype.overlaps = function(bb) {
    return (Math.abs(this.x + this.width/2 - bb.x - bb.width/2) * 2 <= (this.width + bb.width)) &&
        (Math.abs(this.y + this.height/2 - bb.y - bb.height/2) * 2 <= (this.height + bb.height));
};

Geometry.BoundingBox.prototype.overlapArea = function(bb) {
    var min_x = Math.max(this.getXMin(), bb.getXMin());
    var max_x = Math.min(this.getXMax(), bb.getXMax());
    var min_y = Math.max(this.getYMin(), bb.getYMin());
    var max_y = Math.min(this.getYMax(), bb.getYMax());
    return Math.max(max_x - min_x, 0) * Math.max(max_y - min_y, 0);
};

Geometry.BoundingBox.prototype.intersects = function(object) {
    switch (object.constructor) {
        case Geometry.BoundingBox:
            return this.overlaps(object);
        case Geometry.Coord:
            return object.containedByBB(this);
        default:
            if(object.intersects != undefined) {
                return object.intersects(this);
            } else {
                throw new Error("Unknown object");
            }
    }
};

Geometry.BoundingBox.prototype.contains = function(object){
    switch (object.constructor) {
        case Geometry.BoundingBox:
            return this.getXMax() >= object.getXMax() && this.getXMin() <= object.getXMin() &&
                this.getYMax() >= object.getYMax() && this.getYMin() <= object.getYMin();
        default:
            if(object.containedByBB != undefined) {
                return object.containedByBB(this);
            } else {
                throw new Error("Unknown object");
            }
    }
};

Geometry.BoundingBox.prototype._getPoly = function() {
    var result = new Geometry.Polygon();
    result.points.push(this.getMinMin());
    result.points.push(this.getMinMax());
    result.points.push(this.getMaxMax());
    result.points.push(this.getMaxMin());
    return result;
};

Geometry.BoundingBox.prototype.getPoly = function() {
    if(!this._poly) this._poly = this._getPoly();
    return this._poly;
};
Geometry.BoundingBox.prototype.toPolygon = Geometry.BoundingBox.prototype.getPoly;

module.exports = Geometry;
